import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { LEVEL_LABEL, LEVEL_PERMISSION, RECORD_STATUSES } from '@app/shared';
import type { UserDto, UserLevel } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { useNotify } from '../../components/NotifyProvider';
import { StatusChip } from '../../components/StatusChip';
import { ConfirmDialog } from '../../components/dialogs';
import { formatDate } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import { useAuth } from '../auth/AuthProvider';
import { UserFormDialog } from './UserFormDialog';
import { useSetLocked, useUsers } from './api';

const stacked = (primary: string, secondary?: string) => (
  <>
    <Typography sx={{ fontSize: 13 }}>{primary}</Typography>
    {secondary ? (
      <Typography variant="caption" color="text.secondary" display="block">
        {secondary}
      </Typography>
    ) : null}
  </>
);

const location = (u: UserDto) =>
  [u.address.line1, u.address.line2, u.address.city, u.address.state, u.address.zip]
    .filter(Boolean)
    .join(', ');

/** Manage master agent / distributor / retailer / employee (PC120 to PC123). */
export function AgentsPage({ level }: { level: UserLevel }) {
  const notify = useNotify();
  const { user: me } = useAuth();
  const list = useListState({ q: '', status: '' }, 10, '-createdAt');
  const users = useUsers({ ...list.params, level });
  const setLocked = useSetLocked();

  const [form, setForm] = useState<{ user?: UserDto } | null>(null);
  const [lockTarget, setLockTarget] = useState<UserDto | null>(null);

  const label = LEVEL_LABEL[level];
  const lower = label.toLowerCase();
  // Top-level users can only be created by an administrator.
  const canAdd = (level !== 'master' && level !== 'admin') || me?.level === 'admin';

  const confirmLock = async () => {
    if (!lockTarget) return;
    try {
      const updated = await setLocked.mutateAsync({
        id: lockTarget.id,
        locked: !lockTarget.locked,
      });
      notify.success(`${updated.loginId} ${updated.locked ? 'locked' : 'unlocked'}.`);
    } catch (err) {
      notify.error(err);
    } finally {
      setLockTarget(null);
    }
  };

  const chainColumns: Column<UserDto>[] = [];
  if (level === 'master')
    chainColumns.push({ key: 'companyName', header: 'Company name', render: (u) => u.companyName });
  if (level !== 'master')
    chainColumns.push({
      key: 'master',
      header: 'Master agent',
      render: (u) => u.chain.master?.name ?? '',
    });
  if (level === 'retailer' || level === 'employee')
    chainColumns.push({
      key: 'distributor',
      header: 'Distributor name',
      render: (u) => u.chain.distributor?.name ?? '',
    });
  if (level === 'employee')
    chainColumns.push({
      key: 'retailer',
      header: 'Retailer name',
      render: (u) => u.chain.retailer?.name ?? '',
    });

  const columns: Column<UserDto>[] = [
    { key: '#', header: '#', render: (_u, i) => rowNumber(i, list.page, list.pageSize), width: 50 },
    {
      key: 'name',
      header: `${level === 'employee' ? 'Name' : `${label} name`} / Group`,
      sortable: true,
      render: (u) => stacked(u.name, u.group?.name),
    },
    ...chainColumns,
    { key: 'loginId', header: 'Login ID', sortable: true, render: (u) => u.loginId },
    {
      key: 'contact',
      header: 'Email / Address / Location',
      render: (u) => stacked(u.email, location(u)),
    },
    {
      key: 'createdAt',
      header: 'Created date / By',
      sortable: true,
      render: (u) => stacked(formatDate(u.createdAt), u.createdBy?.loginId),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (u) => <StatusChip status={u.status} />,
    },
    {
      key: 'locked',
      header: 'Lock/Unlock',
      render: (u) => (
        <Tooltip title={u.locked ? 'Locked. Click to unlock' : 'Unlocked. Click to lock'}>
          <span>
            <IconButton
              size="small"
              color={u.locked ? 'error' : 'default'}
              onClick={() => setLockTarget(u)}
              disabled={u.id === me?.id}
              aria-label={`${u.locked ? 'Unlock' : 'Lock'} ${u.loginId}`}
            >
              {u.locked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    {
      key: 'disabledAt',
      header: 'Disabled date',
      nowrap: true,
      render: (u) => formatDate(u.disabledAt),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (u) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => setForm({ user: u })}
            aria-label={`Edit ${u.loginId}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h3" sx={{ flexGrow: 1 }}>
          {label} List ({LEVEL_PERMISSION[level]})
        </Typography>
        {canAdd ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setForm({})}>
            {level === 'master' ? 'Add new agent' : `Add new ${lower}`}
          </Button>
        ) : null}
      </Box>
      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          label="Search name, login ID or email"
          value={list.draft.q}
          onChange={(e) => list.setField('q', e.target.value)}
        />
        <TextField
          select
          label="Status"
          value={list.draft.status}
          onChange={(e) => list.setField('status', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {RECORD_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
      </FilterPanel>

      <DataTable
        label={`${label} list`}
        columns={columns}
        rows={users.data?.data}
        rowKey={(u) => u.id}
        loading={users.isFetching}
        error={users.error}
        onRetry={() => void users.refetch()}
        page={list.page}
        pageSize={list.pageSize}
        total={users.data?.total ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        pageSizeOptions={[10, 20, 50, 100]}
        sort={list.sort}
        onSortChange={list.setSort}
      />

      <UserFormDialog
        open={Boolean(form)}
        level={level}
        user={form?.user}
        onClose={() => setForm(null)}
      />

      <ConfirmDialog
        open={Boolean(lockTarget)}
        title={lockTarget?.locked ? 'Unlock user' : 'Lock user'}
        message={
          lockTarget?.locked
            ? `Unlock ${lockTarget.loginId} so they can sign in again?`
            : `Lock ${lockTarget?.loginId}? They will be signed out and cannot sign in until unlocked.`
        }
        confirmLabel={lockTarget?.locked ? 'Unlock' : 'Lock'}
        danger={!lockTarget?.locked}
        busy={setLocked.isPending}
        onConfirm={() => void confirmLock()}
        onClose={() => setLockTarget(null)}
      />
    </Box>
  );
}
